<template>
  <v-row>
    <v-col cols="12">
      <v-card class="biz-card" flat>
        <!-- HEADER BAND -->
        <div class="biz-header pl-base-bg">
          <div class="biz-header-icon">
            <v-icon color="#fff" size="22">mdi-domain</v-icon>
          </div>
          <div>
            <div class="biz-title">Business Profile</div>
            <div class="biz-subtitle">{{ form.businessName || "Your business details for invoicing" }}</div>
          </div>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" class="pa-6">
          <div class="biz-section-label">Business Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.businessName"
                label="Business Name"
                outlined
                dense
                prepend-inner-icon="mdi-office-building"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.gstin"
                label="GSTIN"
                outlined
                dense
                prepend-inner-icon="mdi-file-document"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.email"
                label="Email"
                outlined
                dense
                :rules="[rules.email]"
                prepend-inner-icon="mdi-email"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                label="Phone"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
              />
            </v-col>
          </v-row>

          <div class="biz-section-label mt-2">Address</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.pincode"
                label="Pincode"
                outlined
                dense
                :rules="[rules.pincode]"
                :loading="pincodeLoading"
                maxlength="6"
                prepend-inner-icon="mdi-map-marker-radius"
                @input="onPincodeInput"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.city"
                label="City"
                outlined
                dense
                prepend-inner-icon="mdi-city"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.state"
                label="State"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.country"
                label="Country"
                outlined
                dense
                prepend-inner-icon="mdi-earth"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.address"
                label="Address"
                outlined
                dense
                rows="2"
                prepend-inner-icon="mdi-home"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row v-if="isOwner" class="mt-4" justify="end" align="center" no-gutters>
            <v-btn color="secondary" @click="reset">Reset</v-btn>

            <v-btn
              class="biz-save-btn ml-3"
              :loading="loading"
              @click="submit"
            >
              Save Profile
            </v-btn>
          </v-row>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import { lookupPincode, PINCODE_RULE } from "@/utils/pincode";

export default {
  name: "BusinessProfile",

  data() {
    return {
      valid: false,
      loading: false,
      pincodeLoading: false,
      pincodeDebounce: null,
      form: {
        businessName: "",
        gstin: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        country: "",
      },
      rules: {
        email: (v) => !v || /.+@.+\..+/.test(v) || "Invalid email",
        pincode: PINCODE_RULE,
      },
    };
  },

  computed: {
    profile() {
      return this.$store.state.business?.profile;
    },
  },

  created() {
    this.loadProfile();
  },

  methods: {
    ...mapActions("business", ["getProfile", "saveProfile"]),

    onPincodeInput(value) {
      clearTimeout(this.pincodeDebounce);
      if (!/^\d{6}$/.test(value)) return;

      this.pincodeDebounce = setTimeout(async () => {
        this.pincodeLoading = true;
        const result = await lookupPincode(value);
        this.pincodeLoading = false;

        if (!result) return;
        this.form.city = result.city || this.form.city;
        this.form.state = result.state || this.form.state;
        this.form.country = result.country || this.form.country;
      }, 400);
    },

    async loadProfile() {
      try {
        await this.getProfile();

        if (this.profile) {
          this.form = { ...this.profile };
        }
      } catch (err) {
        console.error(err);
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        await this.saveProfile(this.form);

        this.$toast?.success("Business profile saved successfully");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to save profile",
        );
      } finally {
        this.loading = false;
      }
    },

    reset() {
      if (this.profile) {
        this.form = { ...this.profile };
      }
    },
  },
};
</script>

<style scoped>
.biz-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.biz-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.biz-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.biz-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.biz-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.biz-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.biz-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
